import { useEffect, useCallback, useState  } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/shared/hooks/use-toast';
import { apiClient } from '@/shared/services/api';


const MST_OFFSET = -7; // MST is UTC-7 (or UTC-6 during DST, but we'll use -7 for consistency)

export const useSessionManager = () => {
  const { toast } = useToast();

  // this as the very first effect to hydrate session early
  // 🧠 STEP 1 — Ensure Supabase hydrates its session before anything else runs
  const [isHydrated, setIsHydrated] = useState(false);




  // Get current time in MST
  const getMSTTime = useCallback(() => {
    const now = new Date();
    const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
    const mst = new Date(utc + (MST_OFFSET * 3600000));
    return mst;
  }, []);

  // Check if it's a new day since last login
  const isNewDay = useCallback((lastLoginDate: string) => {
    const lastLogin = new Date(lastLoginDate);
    const currentMST = getMSTTime();
    
    // Convert last login to MST for comparison
    const lastLoginUTC = lastLogin.getTime() + (lastLogin.getTimezoneOffset() * 60000);
    const lastLoginMST = new Date(lastLoginUTC + (MST_OFFSET * 3600000));
    
    // Check if it's a different day
    return currentMST.getDate() !== lastLoginMST.getDate() || 
           currentMST.getMonth() !== lastLoginMST.getMonth() || 
           currentMST.getFullYear() !== lastLoginMST.getFullYear();
  }, [getMSTTime]);

  // Store login timestamp
  const recordLogin = useCallback(() => {
    const currentTime = new Date().toISOString();
    localStorage.setItem('lastLoginTime', currentTime);
  }, []);

  // Check and handle daily reset
  const checkDailyReset = useCallback(async () => {
    const lastLoginTime = localStorage.getItem('lastLoginTime');
    
    if (!lastLoginTime) {
      // First time login, record it
      recordLogin();
      return;
    }

    if (isNewDay(lastLoginTime)) {
      // It's a new day, log the user out
      try {
        await supabase.auth.signOut();
        localStorage.removeItem('lastLoginTime');
        
      } catch (error) {
        console.error('Error during daily reset:', error);
      }
    }
  }, [isNewDay, recordLogin, toast]);

  // ✅ Verify Supabase Database Connection (Phase 1 - Step 4)
  // CRITICAL FIX: Removed - this function is not used and calls getUser() which hangs
  // const verifySupabaseConnection = useCallback(async () => {
  //   const { data: { user }, error: userError } = await supabase.auth.getUser()
  //   ...
  // }, [])

  // Set up periodic checks and auth state monitoring
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    let refreshTokenInterval: NodeJS.Timeout;

    // Function to check for resumed subscriptions
    const checkResumedSubscriptions = async () => {
      try {
        // SECURITY: Use backend proxy instead of calling edge function directly
        await apiClient.post('/edge/resume-paused-subscriptions', {});
      } catch (error) {
        console.error('Error checking resumed subscriptions:', error);
      }
    };

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          // User just logged in, record the time
          recordLogin();

          // OPTIMIZED: Run checks in parallel instead of sequentially
          await Promise.all([
            checkResumedSubscriptions(),
            // verifySupabaseConnection() // REMOVED: Unnecessary verification check
          ]);

          // Start periodic checks every 5 minutes
          intervalId = setInterval(() => {
            checkDailyReset();
          }, 5 * 60 * 1000); // Check every 5 minutes
        } else if (event === 'SIGNED_OUT') {
          // User logged out, clear the interval
          if (intervalId) {
            clearInterval(intervalId);
          }
          localStorage.removeItem('lastLoginTime');
        }
      }
    );

    // Initial check when hook mounts
    // CRITICAL FIX: Use localStorage instead of getSession() to prevent hanging
    const initialCheck = async () => {
      const { hasSession } = await import('@/shared/utils/supabaseStorage');

      if (hasSession()) {
        await checkDailyReset();

        // Check for resumed subscriptions on app load
        await checkResumedSubscriptions();

        // Start periodic checks if user is logged in
        intervalId = setInterval(() => {
          checkDailyReset();
        }, 5 * 60 * 1000);
      }
    };

    initialCheck();

    // Refresh session every 1 hour to keep tokens fresh
    refreshTokenInterval = setInterval(async () => {
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (error) console.error("⚠️ Session refresh failed:", error.message);
        else console.log("🔁 Session refreshed:", data.session?.user?.id);
      } catch (err) {
        console.error("❌ Unexpected error refreshing session:", err);
      }
    }, 60 * 60 * 1000); // every 1 hour

    // Cleanup
    return () => {
      subscription.unsubscribe();
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (refreshTokenInterval) {
        clearInterval(refreshTokenInterval);
      }
    };
  }, [checkDailyReset, recordLogin]);

  // Also check when the window becomes active (user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkDailyReset();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkDailyReset]);

  return {
    recordLogin,
    checkDailyReset,
    getMSTTime,
    isHydrated
  };
};